/**
 * Money helpers for the insurance proposal logic. All proposal math is in whole cents.
 *
 * The Worker (`jobnimbus-mcp`) kept dollars as floats but did every sum and comparison in cents,
 * converting with `Math.round(dollars * 100)` (its `c()` / `cents()` helpers). Converting once, at
 * the boundary where dollars come in (the AI's reading of the carrier estimate, a typed amount),
 * with the same rule gives the same cents as the Worker for any amount with at most two decimals.
 */

/**
 * Dollars → whole cents, exactly as the Worker rounds (`Math.round(n * 100)`).
 * Half-cent float artifacts round the way JavaScript's `Math.round` does (toward +∞), e.g.
 * 1.005 → 100 (because 1.005 * 100 is 100.49999…), 2.675 → 267. Carrier estimates print whole
 * cents, so this only matters for hand-typed amounts with three or more decimals.
 */
export function dollarsToCents(dollars: number): number {
  const c = Math.round(dollars * 100);
  return Object.is(c, -0) ? 0 : c;
}

/** Whole cents → dollars (for display and for prompts that speak dollars). */
export function centsToDollars(cents: number): number {
  return cents / 100;
}

const usd = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });

/** "$1,234.56", "-$5.00": the Worker's `toLocaleString("en-US", { style: "currency", currency: "USD" })`. */
export function formatUsd(cents: number): string {
  return usd.format(cents / 100);
}

/** Sum of a list of cents. */
export function sumCents<T>(list: readonly T[], f: (x: T) => number): number {
  let total = 0;
  for (const x of list) total += f(x);
  return total;
}
